import { useFlag } from '@unleash/proxy-client-react';
import { useState, Dispatch, SetStateAction, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import { SelectChangeEvent } from '@mui/material/Select';
import { ProductDetailsModal } from '../view-product/product-details-modal';
import { productsSelectors } from '../../../store/products';
import {
  DataGridContainer,
  TenantFormControl,
  LocationFormControl,
  StyledTenantSelect,
  StyledLocationSelect,
  StyledTableContainer,
  StyledHeaderRow,
  StyledUPCHeaderCell,
  StyledDisplayNameHeaderCell,
  StyledHeaderCell,
  StyledTableRow,
  StyledTableCell,
  UPCCellContainer,
  UPCImage,
  TooltipImage,
  UPCTypography,
  DisplayNameTypography,
  MoreResultsButton,
  MenuProps,
  StyledMenuItem,
  StyledInputLabel,
  MoreResultsButtonIcon,
  fallBackImageUrl,
  ProgressBar,
  MoreResultsProgressBarContainer,
  MoreResultsProgressBar,
  ImageBox,
  TooltipImageBox,
  EditButton,
  ButtonBox,
  SearchFilterFormControl,
  StyledSearchFilterSelect
  // ProgressBar
} from './data-grid.styles';
import { NoProducts } from './no-products-page';
import { ErrorPage } from './error-page';
import { getProductImageUrl } from '../../../utils/productImage';
import { EditModal } from '../edit-product/edit-modal';
import '../../../index.css';
import { Alert, ErrorSnackbar } from '../edit-product/edit-modal.styles';
import appConfig from '../../../config/app.config';
import { TooltipDisplayName } from '../common/tooltip.styles';
import { ImageTooltip } from '../common/tooltip';
import { singleProductActions } from '../../../store/single-product';
import { capitalize } from 'lodash';
import { SearchFilter } from '../../../store/types';

interface DataGridProps {
  onChange: (event: SelectChangeEvent<any>) => void;
  onMoreResultsButtonClick: () => void;
  setLastRowRef: Dispatch<SetStateAction<HTMLTableRowElement | null>>;
  setFirstRowRef: Dispatch<SetStateAction<HTMLTableRowElement | null>>;
  onRowClick: (productId: string) => void;
  handleSearchFilterChange: (event: SelectChangeEvent<any>) => void;
}

export function DataGrid({
  onChange,
  onMoreResultsButtonClick,
  setLastRowRef,
  setFirstRowRef,
  onRowClick,
  handleSearchFilterChange
}: DataGridProps): JSX.Element {
  const isEditProductEnabled: boolean = useFlag(appConfig.FEATURE_FLAGS.FEATURES.EDIT_PRODUCT);

  const products = useSelector((state: RootState) =>
    productsSelectors.selectProducts(state.products)
  );

  const isRequestLoading = useSelector((state: RootState) =>
    productsSelectors.selectIsDataGridLoading(state.products)
  );

  const isRequestError = useSelector((state: RootState) =>
    productsSelectors.selectErrorOccuredInDataGrid(state.products)
  );

  const tenantsList = useSelector((state: RootState) =>
    productsSelectors.selectTenantsAndLocations(state.products)
  );

  const isMoreProductsLoading = useSelector((state: RootState) =>
    productsSelectors.selectIsMoreProductsLoading(state.products)
  );

  const productsLimit = useSelector((state: RootState) =>
    productsSelectors.selectProductsLimit(state.products)
  );

  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const currentTenant = useSelector((state: RootState) =>
    productsSelectors.selectCurrentTenant(state.products)
  );

  const currentLocation = useSelector((state: RootState) =>
    productsSelectors.selectCurrentLocation(state.products)
  );

  const currentSearchFilter = useSelector((state: RootState) =>
    productsSelectors.selectCurrentSearchFilter(state.products)
  );

  const dispatch = useDispatch();

  const tenantsKeys = Object.keys(tenantsList);

  const searchFilterList: SearchFilter[] = [
    { key: 'all', displayName: 'All products' },
    { key: '"isInStock"=false', displayName: 'Out of stock products' },
    { key: '"isInactive"=true', displayName: 'Inactive products' },
    { key: '"isOnHomepage"=true', displayName: 'On homepage products' }
  ];

  function handleRowClick(productId: string, isEdit: boolean) {
    onRowClick(productId);
    setIsViewModalOpen(true);
  }

  function handleEditClick(event?: any, productId?: string) {
    if (event) {
      event.stopPropagation();
    }
    if (productId) {
      onRowClick(productId);
    }
    setIsEditModalOpen(true);
  }

  function closeViewModal() {
    setIsViewModalOpen(false);
  }

  function closeEditModal(isUpdateSuccess?: boolean) {
    setIsEditModalOpen(false);
    setIsViewModalOpen(false);
    dispatch(singleProductActions.resetModifiers());
    if (isUpdateSuccess) setIsSuccess(true);
  }

  function refConditions(index: number, productsLimit: number): boolean {
    return index === productsLimit - 100 || (index === 0 && productsLimit === 100);
  }

  function isNoTenantSelected(): string {
    return currentTenant === '' ? '' : '#5F65F6';
  }

  function checkAtLeastOneProduct() {
    return products.length > 0;
  }

  function renderNoProductsConditions(): boolean {
    return !checkAtLeastOneProduct() && !isRequestLoading && !isRequestError;
  }

  function renderProductsConditions(): boolean {
    return checkAtLeastOneProduct() && !isRequestLoading && !isRequestError;
  }

  return (
    <Fragment>
      <ErrorSnackbar
        id='success-update-notification'
        open={isSuccess}
        autoHideDuration={3000}
        onClose={() => setIsSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity='success'
          onClose={() => setIsSuccess(false)}
        >{`Product successfully updated`}</Alert>
      </ErrorSnackbar>
      <DataGridContainer>
        {isRequestLoading && <ProgressBar id='progress-bar' />}
        <TenantFormControl size='small'>
          <StyledInputLabel id='tenant-select-label'>Store brand</StyledInputLabel>
          <StyledTenantSelect
            name='tenantId'
            id='tenant-select'
            value={tenantsList[currentTenant] ? currentTenant : 'All'}
            label='Store brand'
            onChange={onChange}
            MenuProps={MenuProps}
          >
            <StyledMenuItem key='none-tenant' value='All'>
              All brands
            </StyledMenuItem>
            {tenantsKeys.map((tenant) => (
              <StyledMenuItem
                key={tenantsList[tenant].tenantId}
                value={tenantsList[tenant].tenantId}
              >
                {tenantsList[tenant].tenantName}
              </StyledMenuItem>
            ))}
          </StyledTenantSelect>
        </TenantFormControl>
        <LocationFormControl
          size='small'
          sx={{
            // Conditional hover that depends on the value, hard to move outside
            '&:hover': {
              '& .MuiInputLabel-root': {
                color: isNoTenantSelected()
              }
            }
          }}
        >
          <StyledInputLabel id='location-select-label'>Store location</StyledInputLabel>
          <StyledLocationSelect
            name='locationId'
            id='location-select'
            value={tenantsList[currentTenant] && currentLocation ? currentLocation : 'All'}
            label='Store location'
            disabled={currentTenant === ''}
            onChange={onChange}
            MenuProps={MenuProps}
            sx={{
              // Conditional hover that depends on the value, hard to move outside
              '&:hover': {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isNoTenantSelected()
                }
              }
            }}
          >
            <StyledMenuItem key='none-location' value='All'>
              All stores
            </StyledMenuItem>
            {currentTenant !== '' &&
              tenantsList[currentTenant] &&
              tenantsList[currentTenant].locations.map((location) => {
                return (
                  <StyledMenuItem key={location.locationId} value={location.locationId}>
                    {location.locationName}
                  </StyledMenuItem>
                );
              })}
          </StyledLocationSelect>
        </LocationFormControl>
        <SearchFilterFormControl size='small'>
          <StyledInputLabel id='search-filter-select-label'>View</StyledInputLabel>
          <StyledSearchFilterSelect
            name='searchFilter'
            id='search-filter-select'
            value={currentSearchFilter}
            label='View'
            onChange={handleSearchFilterChange}
            MenuProps={MenuProps}
          >
            {searchFilterList.map((searchFilter) => (
              <StyledMenuItem id={searchFilter.key} key={searchFilter.key} value={searchFilter.key}>
                {searchFilter.displayName}
              </StyledMenuItem>
            ))}
          </StyledSearchFilterSelect>
        </SearchFilterFormControl>
        <Fragment>
          <StyledTableContainer>
            <Table id='main-table' aria-label='simple table' stickyHeader>
              <TableHead id='table-head'>
                <StyledHeaderRow id='table-header-row'>
                  <StyledUPCHeaderCell id='table-header-upc-cell' align='left'>
                    UPC
                  </StyledUPCHeaderCell>
                  <StyledDisplayNameHeaderCell id='table-header-display-name-cell' align='left'>
                    Product display name
                  </StyledDisplayNameHeaderCell>
                  <StyledHeaderCell id='table-header-category-cell' align='left'>
                    Category
                  </StyledHeaderCell>
                  <StyledHeaderCell id='table-header-sub-category-cell' align='left'>
                    Sub category
                  </StyledHeaderCell>
                  <StyledHeaderCell id='table-header-tenant-cell' align='left'>
                    Store brand
                  </StyledHeaderCell>
                  <StyledHeaderCell id='table-header-location-cell' align='left'>
                    Store location
                  </StyledHeaderCell>
                  <StyledHeaderCell
                    id='table-header-edit-cell'
                    style={{ width: '20%' }}
                  ></StyledHeaderCell>
                </StyledHeaderRow>
              </TableHead>
              <TableBody id='table-body'>
                {renderProductsConditions() &&
                  products.map((row, index) => (
                    <StyledTableRow
                      className='rowOnHover'
                      id={`product-row-${row.productId}`}
                      onClick={() => handleRowClick(row.productId, false)}
                      key={row.productId}
                      hover
                      // Ref to make table to load
                      ref={
                        refConditions(index, productsLimit)
                          ? (ref) => {
                              if (index === 0) {
                                setFirstRowRef(ref);
                              } else {
                                setLastRowRef(ref);
                              }
                            }
                          : null
                      }
                    >
                      <StyledTableCell
                        id={`${row.productId}-upc-cell`}
                        align='left'
                        component='td'
                        scope='row'
                      >
                        <UPCCellContainer>
                          <ImageTooltip
                            id={`${row.productId}`}
                            value={
                              <TooltipImageBox>
                                <img
                                  src={getProductImageUrl(row) ?? fallBackImageUrl}
                                  alt={row.displayName}
                                  onError={(e) => (e.currentTarget.src = fallBackImageUrl)}
                                  style={TooltipImage}
                                />
                              </TooltipImageBox>
                            }
                            child={
                              <ImageBox>
                                <img
                                  src={getProductImageUrl(row) ?? fallBackImageUrl}
                                  alt={row.displayName}
                                  onError={(e) => (e.currentTarget.src = fallBackImageUrl)}
                                  style={UPCImage}
                                />
                              </ImageBox>
                            }
                          />
                          <UPCTypography>
                            {row.upc && row.upc.length > 0 ? row.upc : 'N/A'}
                          </UPCTypography>
                        </UPCCellContainer>
                      </StyledTableCell>
                      <StyledTableCell id={`${row.productId}-display-name-cell`} align='left'>
                        <TooltipDisplayName
                          id='data-grid-display-name'
                          value={row.displayName}
                          minCharacters={38}
                          child={<DisplayNameTypography>{row.displayName}</DisplayNameTypography>}
                        />
                      </StyledTableCell>
                      <StyledTableCell id={`${row.productId}-category-cell`} align='left'>
                        {capitalize(row.type)}
                      </StyledTableCell>
                      <StyledTableCell id={`${row.productId}-sub-category-cell`} align='left'>
                        {capitalize(row.subType) || 'N/A'}
                      </StyledTableCell>
                      <StyledTableCell id={`${row.productId}-tenant-cell`} align='left'>
                        {row.tenantName}
                      </StyledTableCell>
                      <StyledTableCell id={`${row.productId}-location-cell`} align='left'>
                        {row.locationName}
                      </StyledTableCell>
                      <StyledTableCell>
                        {isEditProductEnabled && (
                          <ButtonBox>
                            <EditButton
                              id={`data-grid-edit-button-${row.productId}`}
                              onClick={(event) => handleEditClick(event, row.productId)}
                            >
                              Edit
                            </EditButton>
                          </ButtonBox>
                        )}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
              </TableBody>
            </Table>
            {products.length === productsLimit &&
              !isRequestLoading &&
              !isMoreProductsLoading &&
              !isRequestError && (
                <MoreResultsButton
                  id='more-results-button'
                  onClick={onMoreResultsButtonClick}
                  disableRipple={true}
                  endIcon={<MoreResultsButtonIcon />}
                >
                  Show more results
                </MoreResultsButton>
              )}
            {isRequestError && <ErrorPage />}
            {renderNoProductsConditions() && <NoProducts />}
            {isMoreProductsLoading && !isRequestLoading && !isRequestError && (
              <MoreResultsProgressBarContainer>
                <MoreResultsProgressBar id='more-results-progress-bar' />
              </MoreResultsProgressBarContainer>
            )}
          </StyledTableContainer>
          {isViewModalOpen && (
            <ProductDetailsModal
              isOpen={isViewModalOpen}
              onClose={closeViewModal}
              onEditClick={handleEditClick}
            />
          )}
          {isEditModalOpen && (
            <EditModal isOpen={isEditModalOpen} onClose={closeEditModal} isAdd={false} />
          )}
        </Fragment>
      </DataGridContainer>
    </Fragment>
  );
}
